<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import FilterMultiCombobox from '@/components/FilterBuilder/FilterMultiCombobox.vue';
import TagFilterOption from '@/components/FilterBuilder/TagFilterOption.vue';
import { useFilterOptions, type TagFilterOptionShape } from '@/composables/useFilterOptions';
import type { BulkOperation } from '@/composables/api/useBulkActionApi';

/**
 * Pick the tags to add to, or remove from, every selected custom list.
 *
 * It reuses the filter builder's tag picker: the same grouped catalog of non-track tags, drawn
 * with the same kind colours. The filter sends tag names, but the bulk endpoint wants ids, so
 * this picker's values are the tag ids.
 */
const props = defineProps<{
  action: 'add_tag' | 'remove_tag';
  count: number;
}>();

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  (e: 'submit', payload: { operation: BulkOperation; valueLabels: Record<string, string> }): void;
}>();

// Custom lists carry non-track tags, the same set their search filter offers.
const { data: tagOptions, isLoading } = useFilterOptions(computed(() => 'tags'));

const options = computed(() => tagOptions.value as TagFilterOptionShape[]);
const chosenTagIds = ref<string[]>([]);
const error = ref<string | null>(null);

watch(open, (isOpen) => {
  if (isOpen) {
    chosenTagIds.value = [];
    error.value = null;
  }
});

const title = computed(() => (props.action === 'add_tag' ? 'Add tags' : 'Remove tags'));

function submit() {
  const tagIds = chosenTagIds.value.map(Number).filter((id) => Number.isInteger(id) && id > 0);
  if (tagIds.length === 0) {
    error.value = 'Choose at least one tag.';
    return;
  }
  const valueLabels: Record<string, string> = {};
  for (const option of options.value) {
    if (tagIds.includes(option.id)) valueLabels[`tag:${option.id}`] = option.name;
  }
  emit('submit', { operation: { action: props.action, tag_ids: tagIds }, valueLabels });
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent data-testid="bulk-tag-dialog">
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription>
          {{ action === 'add_tag' ? 'These tags are added to' : 'These tags are removed from' }}
          {{ count.toLocaleString() }} list{{ count === 1 ? '' : 's' }}. Nothing is saved until you confirm on the next step.
        </DialogDescription>
      </DialogHeader>

      <FilterMultiCombobox
        v-model="chosenTagIds"
        :options="options"
        :get-value="(option: TagFilterOptionShape) => String(option.id)"
        :get-label="(option: TagFilterOptionShape) => option.name"
        :filter-by-query="(option: TagFilterOptionShape, query: string) =>
          option.name.toLowerCase().includes(query.toLowerCase()) ||
          (option.groupName ?? '').toLowerCase().includes(query.toLowerCase())"
        :option-component="TagFilterOption"
        :is-loading="isLoading"
        placeholder="Choose tags…"
        test-id-prefix="bulk-tag-picker"
      />

      <p v-if="error" class="text-destructive text-sm" data-testid="bulk-tag-error">{{ error }}</p>

      <DialogFooter>
        <Button variant="outline" @click="open = false">Cancel</Button>
        <Button data-testid="bulk-tag-submit" @click="submit">Check the changes</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
